import React, { type PropsWithChildren } from "react"
import axios, { type AxiosInstance } from "axios"
import { applyMockInterceptor } from "@/mocks/api-adapter"
import { useRoleInfo } from "@/user/session"
import { triggerLogin } from "@/utils/loginTrigger"

// Fallback role used when user is not yet authenticated in mock/dev mode.
// Keeps recall-frame SWR keys non-null so data fetching starts immediately.
const MOCK_ROLE = {
    uid: "000000",
    nickName: "test#0000",
    channelMasterId: 1 as const,
}

const isMockMode = __MOCK_ENABLED || !__ACTIVITY_CID

const toRole = (user: ReturnType<typeof useRoleInfo>) =>
    user
        ? { uid: user.userId, nickName: user.nickname ?? "", channelMasterId: 1 as const }
        : isMockMode
          ? MOCK_ROLE   // ← 保持 SWR key 非 null，数据层正常运转
          : null        // ← 生产环境：未登录则不拉取

export const DEFAULT_ACCOUNT_TOKEN_HEADER = "x-account-token"
export const DEFAULT_ROLE_TOKEN_HEADER = "x-role-token"

export const createRequest = (
    option?: { requestOption?: Record<string, unknown> },
): AxiosInstance => {
    const instance = axios.create({
        responseType: "json",
        timeout: 5000,
        ...(option?.requestOption || option),
    })

    instance.interceptors.request.use(
        (config) => {
            document.body.classList.add("wait")
            return config
        },
        (error) => {
            document.body.classList.remove("wait")
            return Promise.reject(error)
        },
    )

    // NOTE: No response interceptor here — @hg/recall-frame's request.js adds its own
    // that unwraps response.data. Adding one here would double-unwrap and break the
    // { code, data, message } envelope that library code relies on.

    if (__MOCK_ENABLED) {
        // No real server — serve mock data
        applyMockInterceptor(instance)
    } else {
        // Real server — rewrite recall-frame's first-party URLs to third-party format.
        // recall-frame calls /api/activity/${activityId}/ENDPOINT; map to
        // /api/activities/strange-go-game/ENDPOINT so requests reach the real API.
        instance.interceptors.request.use((config) => {
            if (config.url) {
                config.url = config.url.replace(
                    /\/api\/activity\/[^/]*\//,
                    "/api/activities/strange-go-game/",
                )
            }
            return config
        })
    }

    return instance
}

export const useOneAccount = () => {
    const user = useRoleInfo()
    return { account: toRole(user), loading: false }
}
export const useRole = () => {
    const user = useRoleInfo()
    return { role: toRole(user), loading: false }
}
export const useRoleMeta = () => {
    const user = useRoleInfo()
    return user
        ? { channel: "jp", token: user.userId, verifier: "" }
        : { channel: "mock", token: "", verifier: "" }
}
export const useAccount = () => {
    const user = useRoleInfo()
    return { account: toRole(user), ready: true }
}
export const useAccountLogout = () => () => {}
export const useRoleLogout = () => () => {}
export const useShowChannelSelect = () => triggerLogin
export const useShowRoleSelect = () => triggerLogin

export const OneAccount = {
    hooks: { useShowLoginDialog: () => triggerLogin },
}

export type RoleInfo = {
    uid: string
    nickName: string
    channelMasterId?: number
    channelId?: number
}

export const RoleProvider = ({ children }: PropsWithChildren) => <>{children}</>
